import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CallAPIPostPromise } from "Common/commaon";
import { getDecodeId } from "slices/userProfile/thunk";
import { ProjectInviteModal } from "./ProjectInviteModal";
import { HireConfirmationModal } from "./HireConfirmationModal";
import { ContractorListSkeleton } from "components/shimmer";
import { Container } from "react-bootstrap";
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Building2,
  MapPin,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useSelector } from "react-redux";
import { selectToken } from "slices/auth/login/reducer";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import "../recentContractors/recent-contractors.css";
import "./suggested-contractors.css";

const FETCH_SUGGESTIONS_PATH = "/agent/suggest-contractors/homeowner";
const FETCH_HOMEOWNER_PROJECTS_PATH = "/jobs/homeownerJobs";
const SEND_PROJECT_INVITE_PATH = "/jobs/sendContractorRequestForJob";
const MARK_SUGGESTION_HIRED_PATH = "/agent/suggest-contractors/hired";
const RELIBUILD_LOGO = "https://tradesmen.s3.amazonaws.com/relibuild-logo/relibuild-logo.png";

function clean(value: any) {
  const text = String(value ?? "").trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
}

function parseLabels(value: any) {
  if (!value) return [];

  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return value
        .replace(/^\[|\]$/g, "")
        .replace(/"/g, "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }

  const items = Array.isArray(parsed) ? parsed : [parsed];
  return items
    .map((item: any) =>
      typeof item === "string" ? item : item?.label || item?.value || item?.name || ""
    )
    .map(clean)
    .filter(Boolean);
}

function parseAvailability(rawAvailability: any) {
  if (!rawAvailability) return "";

  if (typeof rawAvailability === "string") {
    try {
      const parsed = JSON.parse(rawAvailability);

      if (Array.isArray(parsed)) {
        return parsed
          .map((item: any) =>
            typeof item === "string" ? item : item?.label || item?.value || ""
          )
          .filter(Boolean)
          .join(", ");
      }

      if (typeof parsed === "object" && parsed !== null) {
        return parsed.label || parsed.value || "";
      }

      return typeof parsed === "string" ? parsed : "";
    } catch {
      return rawAvailability.trim();
    }
  }

  if (Array.isArray(rawAvailability)) {
    return rawAvailability
      .map((item: any) =>
        typeof item === "string" ? item : item?.label || item?.value || ""
      )
      .filter(Boolean)
      .join(", ");
  }

  if (typeof rawAvailability === "object") {
    return rawAvailability.label || rawAvailability.value || "";
  }

  return "";
}
interface HomeownerProject {
  id: string;
  name: string;
  description: string;
  budget: string;
  timeline: string;
}
interface PastJob {
  id: string;
  title: string;
  location: string;
  completedDate: string;
  type: string;
}
interface Contractor {
  id: string;
  name: string;
  logo?: string;
  services: string[];
  location: string;
  rating: number;
  completedJobs: number;
  yearsExperience: number;
  availability: string;
  priceRange: string;
  description: string;
  pastJobs: PastJob[];
  portfolioImages: string[];
  suggestionId?: number;
  agentUserId?: number;
  homeownerUserId?: number;
  jobId?: number | null;
  contractorUserId?: number;
  note?: string;
  status?: string;
  companyName?: string;
  profile?: string;
  email?: string;
  phone?: string;
  refType?: string;
  agentName?: string;
}
  
export function SuggestedContractrDashboard() {
  const navigate = useNavigate();
  const [inviteContractor, setInviteContractor] = useState<Contractor | null>(null);
  const [hireContractor, setHireContractor] = useState<Contractor | null>(null);
  const [suggestedContractors, setSuggestedContractors] = useState<any[]>([]);
  const [projects, setProjects] = useState<HomeownerProject[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [hireSending, setHireSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("recommended");
  const homeownerId = Number(getDecodeId() || 0);
const token = useSelector(selectToken)

  useEffect(() => {
    if (!homeownerId) {
      return;
    }

    setLoading(true);
    Promise.all([
      CallAPIPostPromise(FETCH_SUGGESTIONS_PATH, {
        homeownerUserId: homeownerId,
      }),
      CallAPIPostPromise(FETCH_HOMEOWNER_PROJECTS_PATH, {
        homeowner_id: homeownerId,
      }),
    ])
      .then(([suggestionsResp, projectsResp]: any) => {
        const suggestionPayload = suggestionsResp?.data;
        const suggestionRows = Array.isArray(suggestionPayload)
          ? suggestionPayload
          : suggestionPayload?.data || [];
        const mappedSuggestions = suggestionRows.map((row: any) => {
          const parsedProfiles = parseLabels(row.ua_profile);
          const parsedProfile = parsedProfiles[0] || "General Contractor";
          const parsedSkills = parseLabels(row.ua_skills);
        
          const parsedAvailability = parseAvailability(row.ua_work_availbility);
        
          return {
            id: String(row.acs_contractor_user_id || row.id),
        
            // 👤 Name
            name:
              `${clean(row.user_first_name)} ${clean(row.user_last_name)}`.trim() ||
              clean(row.cc_company_name) ||
              "Contractor",
        
            // 🖼️ Images
            logo: clean(row.ua_profile_pic) || clean(row.cc_logo_url),
            companyName: clean(row.cc_company_name),
        
            // 🧠 Profile
            profile: parsedProfile,
        
            // 🛠️ Services
            services: parsedSkills,
        
            // 📍 Location
            location: [
              clean(row.ua_city) || clean(row.cc_city),
              clean(row.ua_state) || clean(row.cc_state),
              clean(row.ua_country) || clean(row.cc_country),
            ]
              .filter(Boolean)
              .join(", ") || "Unknown",
        
            // ⭐ Stats
            rating: Number(row.rating || 0),
            completedJobs: Number(row.ua_completed || 0),
            yearsExperience: Number(row.ua_exp || 0),
        
            // ⏱️ Availability
            availability: parsedAvailability,
        
            // 💰 Pricing
            priceRange: row.price_range || "",
        
            // 📝 Description (FIXED)
            description:
              clean(row.ua_description) ||
              parsedProfile ||
              "No profile description available.",
        
            // 📞 Contact
            email: clean(row.user_email),
            phone: clean(row.user_phone),
        
            // 📂 Extras
            pastJobs: [],
            portfolioImages: [],
        
            // 🔗 Suggestion metadata
            suggestionId: Number(row.acs_id),
            agentUserId: Number(row.acs_agent_user_id || 0),
            homeownerUserId: Number(row.acs_homeowner_user_id || homeownerId),
            jobId: row.acs_job_id ? Number(row.acs_job_id) : null,
            contractorUserId: Number(row.acs_contractor_user_id || row.id),
            note: row.acs_note || "",
            status: row.acs_status || "suggested",
            refType: row.ref_type || "unknown",
            agentName:
              row.agent_name ||
              `${row.agent_first_name || ""} ${row.agent_last_name || ""}`.trim() ||
              "Agent",
          };
        });

        setSuggestedContractors(mappedSuggestions);

        const projectRows = projectsResp?.data || [];
        const mappedProjects = projectRows.map((project: any) => ({
          id: String(project.id),
          name: project.job_title || `Project #${project.id}`,
          description: project.job_description || "Project details not provided",
          budget: project.budget ? `$${project.budget}` : "Budget not provided",
          timeline: project.created_at
            ? `Created ${new Date(project.created_at).toLocaleDateString()}`
            : "Timeline not provided",
        }));

        setProjects(mappedProjects);
      })
      .catch((error: any) => {
        console.log(error);
        toast.error(
          error?.response?.data?.message || "Failed to load suggested contractors"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [homeownerId]);

  const visibleContractors = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const filtered = suggestedContractors.filter((contractor) => {
      if (!query) return true;
      return [
        contractor.name,
        contractor.profile,
        contractor.companyName,
        contractor.location,
        contractor.agentName,
        ...(Array.isArray(contractor.services) ? contractor.services : []),
      ].some((value) => clean(value).toLowerCase().includes(query));
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "name") return clean(a.name).localeCompare(clean(b.name));
      if (sortBy === "experience") return Number(b.yearsExperience || 0) - Number(a.yearsExperience || 0);
      return Number(b.refType === "primary") - Number(a.refType === "primary");
    });
  }, [searchQuery, sortBy, suggestedContractors]);

  const handleSendInvite = (projectId: string, message: string) => {
    if (!inviteContractor) {
      return;
    }

    setInviteSending(true);
    CallAPIPostPromise(SEND_PROJECT_INVITE_PATH, {
      contractor_ids: [inviteContractor.contractorUserId || Number(inviteContractor.id)],
      job_id: Number(projectId),
      admin_id: homeownerId,
      homeowner_id: homeownerId,
      message,
    })
      .then(() => {
        toast.success(`Invite sent to ${inviteContractor.name}`);
        setInviteContractor(null);
      })
      .catch((error: any) => {
        console.log(error);
        toast.error(error?.response?.data?.message || "Failed to send invite");
      })
      .finally(() => {
        setInviteSending(false);
      });
  };

  const handleConfirmHire = () => {
    if (!hireContractor?.suggestionId) {
      toast.error("Suggestion information is missing");
      return;
    }

    setHireSending(true);
    CallAPIPostPromise(MARK_SUGGESTION_HIRED_PATH, {
      suggestionId: hireContractor.suggestionId,
      jobId: hireContractor.jobId,
      contractAmount: 0,
      commissionType: "percentage",
      commissionValue: 5,
      createdBy: homeownerId,
    })
      .then(() => {
        toast.success(`Hiring request sent to ${hireContractor.name}`);
        setSuggestedContractors((prev) =>
          prev.filter((contractor) => contractor.suggestionId !== hireContractor.suggestionId)
        );
        setHireContractor(null);
      })
      .catch((error: any) => {
        console.log(error);
        toast.error(error?.response?.data?.message || "Failed to confirm hire");
      })
      .finally(() => {
        setHireSending(false);
      });
  };
  return (
    <div className="page-content tailwind-prefix recent-contractors-page suggested-contractors-page">
      <Container fluid>
        <header className="recent-contractors__toolbar suggested-contractors__toolbar">
          <div className="container mx-auto px-4 py-4">
            <div className="suggested-contractors__intro">
              <span><Sparkles aria-hidden="true" /> Curated for you</span>
              <div>
                <h1>Suggested contractors</h1>
                <p>Review professionals recommended by your RELiBUILD agent.</p>
              </div>
            </div>
            <div className="suggested-contractors__tools">
              <div className="recent-contractors__search app-search-field">
                <Search className="app-field-icon" aria-hidden="true" />
                <Input
                  placeholder="Search by name, company, specialty, or skill..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="recent-contractors__sort suggested-contractors__sort">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger><SelectValue placeholder="Sort by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recommended">Recommended first</SelectItem>
                    <SelectItem value="experience">Most experienced</SelectItem>
                    <SelectItem value="name">Name (A–Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto py-6 suggested-contractors__content">
          {loading ? <ContractorListSkeleton /> : null}

          {!loading && suggestedContractors.length === 0 ? (
            <div className="recent-contractors__empty suggested-contractors__empty">
              <span><ShieldCheck aria-hidden="true" /></span>
              <h2>No suggestions yet</h2>
              <p>Your agent will send contractor recommendations here when they are ready.</p>
            </div>
          ) : null}

          {!loading && suggestedContractors.length > 0 ? (
            <>
              <div className="recent-contractors__count">
                <strong>{visibleContractors.length}</strong>
                <span>{visibleContractors.length === 1 ? "contractor" : "contractors"}</span>
                {visibleContractors.length !== suggestedContractors.length ? <small>of {suggestedContractors.length} suggestions</small> : <small>recommended for you</small>}
              </div>

              <div className="recent-contractors__grid suggested-contractors__grid">
                {visibleContractors.map((contractor: Contractor) => (
                  <SuggestedContractorCard
                    key={contractor.id}
                    contractor={contractor}
                    onViewProfile={() => navigate(`/recent-contractors/${contractor.id}?from=suggested-contractors`)}
                    onHire={() => {
                      const params = new URLSearchParams();
                      if (token) params.set("token", token);
                      if (contractor.suggestionId) params.set("suggestionId", String(contractor.suggestionId));
                      window.open(`https://www.relibuild.com/agent-details/${contractor.id}?${params.toString()}`, "_blank", "noopener,noreferrer");
                    }}
                  />
                ))}
              </div>

              {visibleContractors.length === 0 ? (
                <div className="recent-contractors__empty suggested-contractors__empty suggested-contractors__empty--search">
                  <Search aria-hidden="true" />
                  <h2>No matching contractors</h2>
                  <p>Try a different name, company, specialty, or skill.</p>
                  <Button variant="outline" onClick={() => setSearchQuery("")}>Clear search</Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>

    {inviteContractor && (
      <ProjectInviteModal
        contractor={inviteContractor}
        projects={projects}
        sending={inviteSending}
        onSendInvite={handleSendInvite}
        onClose={() => setInviteContractor(null)}
      />
    )}

    {hireContractor && (
      <HireConfirmationModal
        contractor={hireContractor}
        hiring={hireSending}
        onConfirmHire={handleConfirmHire}
        onClose={() => setHireContractor(null)}
      />
    )}
      </Container>
    </div>
  );
}

function SuggestedContractorCard({
  contractor,
  onViewProfile,
  onHire,
}: {
  contractor: Contractor;
  onViewProfile: () => void;
  onHire: () => void;
}) {
  const services = Array.isArray(contractor.services) ? contractor.services.filter(Boolean) : [];
  const availability = clean(contractor.availability) || "Availability not provided";

  return (
    <article className={`recent-contractor-card suggested-contractor-card${contractor.refType === "primary" ? " suggested-contractor-card--primary" : ""}`}>
      <div className="suggested-contractor-card__recommendation">
        <ShieldCheck aria-hidden="true" />
        <span>{contractor.refType === "primary" ? "Recommended by your RELiBUILD agent" : "Recommended by agent"}</span>
        <strong>{contractor.agentName || "Agent"}</strong>
      </div>

      <div className="recent-contractor-card__header">
        <ContractorAvatar contractor={contractor} />
        <div className="recent-contractor-card__identity">
          <h3>{contractor.name || "Unnamed contractor"}</h3>
          <p>{clean(contractor.profile) || "General contractor"}</p>
          <div className="recent-contractor-card__location"><MapPin aria-hidden="true" /><span>{contractor.location || "Location not provided"}</span></div>
        </div>
        <span className="suggested-contractor-card__status"><Sparkles aria-hidden="true" /> Suggested</span>
      </div>

      <div className="recent-contractor-card__context">
        {contractor.companyName ? (
          <span className="recent-contractor-card__related suggested-contractor-card__company"><Building2 aria-hidden="true" /><span>{contractor.companyName}</span></span>
        ) : null}
        <span className="recent-contractor-card__availability"><i aria-hidden="true" />{availability}</span>
      </div>

      {contractor.yearsExperience > 0 ? (
        <div className="suggested-contractor-card__experience"><BriefcaseBusiness aria-hidden="true" /> {contractor.yearsExperience} years experience</div>
      ) : null}

      {services.length ? (
        <div className="recent-contractor-card__skills" aria-label="Skills">
          {services.slice(0, 3).map((service, index) => <Badge key={`${contractor.id}-service-${index}`} variant="secondary">{clean(service)}</Badge>)}
          {services.length > 3 ? <small>+{services.length - 3} more</small> : null}
        </div>
      ) : null}

      <footer className="recent-contractor-card__footer suggested-contractor-card__footer">
        <button type="button" className="suggested-contractor-card__secondary" onClick={onViewProfile}>View profile</button>
        <button type="button" className="recent-contractor-card__action" onClick={onHire}>Hire contractor <ArrowUpRight aria-hidden="true" /></button>
      </footer>
    </article>
  );
}

function ContractorAvatar({ contractor }: { contractor: Contractor }) {
  const [imageFailed, setImageFailed] = useState(false);
  const image = clean(contractor.logo);

  if (image && !imageFailed) {
    return (
      <img
        src={image}
        alt={`${contractor.name || "Contractor"} profile`}
        className="suggested-contractor-card__avatar-image"
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div className="suggested-contractor-card__avatar-fallback">
      <img src={RELIBUILD_LOGO} alt="RELiBUILD" />
    </div>
  );
}
